import type { TrackEvent } from '@newfront-insurance/data-layer-client';

import type { SkillCommand } from '../../../lib/skill-constants';
import type { NovaSkill } from '../../../queries/chats';

export const NOVA_ANALYTICS_EVENTS = {
  ADD_SKILL_CLICKED_FROM_SKILL_MENU: 'Nova Add Skill Clicked From Skill Menu',
  ASSISTANT_MESSAGE_COPY_BUTTON_CLICKED: 'Nova Assistant Message Copy Button Clicked',
  CHAT_FILE_ATTACHED: 'Nova Chat File Attached',
  CHAT_FILE_ATTACHMENT_FAILED: 'Nova Chat File Attachment Failed',
  CHAT_FILE_ATTACHMENT_STARTED: 'Nova Chat File Attachment Started',
  CHAT_FILE_DOWNLOADED: 'Nova Chat File Downloaded',
  CHAT_INPUT_PASTED: 'Nova Chat Input Pasted',
  CHAT_SELECTION_COPIED: 'Nova Chat Selection Copied',
  GENERATED_SKILL_DOWNLOADED: 'Nova Generated Skill Downloaded',
  GENERATED_SKILL_SAVE_FAILED: 'Nova Generated Skill Save Failed',
  GENERATED_SKILL_SAVE_STARTED: 'Nova Generated Skill Save Started',
  GENERATED_SKILL_SAVE_SUCCEEDED: 'Nova Generated Skill Save Succeeded',
  SKILL_COMMAND_SELECTED: 'Nova Skill Command Selected',
  SKILL_COMMAND_EXPANDED: 'Nova Skill Command Expanded',
  SKILL_COMMAND_SUBMITTED: 'Nova Skill Command Submitted',
  SKILL_CREATION_FAILED: 'Nova Skill Creation Failed',
  SKILL_CREATION_STARTED: 'Nova Skill Creation Started',
  SKILL_CREATION_SUCCEEDED: 'Nova Skill Creation Succeeded',
  SKILL_CREATION_VALIDATION_FAILED: 'Nova Skill Creation Validation Failed',
  SKILL_INVOCATION_SUBMITTED: 'Nova Skill Invocation Submitted',
  SKILL_MENU_OPENED: 'Nova Skill Menu Opened',
  SUGGESTED_PROMPT_CLICKED: 'Nova Suggested Prompt Clicked',
} as const;

export type NovaAnalyticsEvent = (typeof NOVA_ANALYTICS_EVENTS)[keyof typeof NOVA_ANALYTICS_EVENTS];

type AnalyticsProperties = Record<string, unknown>;
interface NovaAnalyticsClient {
  track: (event: TrackEvent) => Promise<void>;
}

export function trackNovaEvent(
  analytics: NovaAnalyticsClient,
  event: NovaAnalyticsEvent,
  properties?: AnalyticsProperties,
) {
  void analytics.track({
    event,
    properties,
  });
}

export function getLengthBucket(length: number | null | undefined): string {
  if (!length || length <= 0) return '0';
  if (length <= 20) return '1-20';
  if (length <= 100) return '21-100';
  if (length <= 500) return '101-500';
  if (length <= 2_000) return '501-2000';
  if (length <= 10_000) return '2001-10000';
  return '10000+';
}

export function getCountBucket(count: number | null | undefined): string {
  if (!count || count <= 0) return '0';
  if (count === 1) return '1';
  if (count <= 3) return '2-3';
  if (count <= 10) return '4-10';
  return '10+';
}

export function getFileSizeBucket(size: number | null | undefined): string {
  if (!size || size <= 0) return '0';
  if (size <= 100_000) return '0-100kb';
  if (size <= 1_000_000) return '100kb-1mb';
  if (size <= 10_000_000) return '1mb-10mb';
  if (size <= 50_000_000) return '10mb-50mb';
  return '50mb+';
}

export function getFileExtension(filename?: string | null): string | undefined {
  const dotIndex = filename?.lastIndexOf('.') ?? -1;
  return dotIndex >= 0 ? filename?.slice(dotIndex + 1).toLowerCase() : undefined;
}

export function getFileAnalyticsProperties(file: File) {
  return {
    fileExtension: getFileExtension(file.name),
    fileSizeBucket: getFileSizeBucket(file.size),
    fileType: file.type || undefined,
  };
}

export function getSkillCommandAnalyticsProperties(command: SkillCommand) {
  return {
    commandHandle: command.handle,
    commandId: command.skill?.id ?? command.id,
    commandKind: 'skill',
    category: command.category,
    skillId: command.skill?.id,
    skillSource: command.skill?.source,
  };
}

export function getSkillAnalyticsProperties(skill: Pick<NovaSkill, 'id' | 'source' | 'command' | 'category'>) {
  return {
    skillId: skill.id,
    skillSource: skill.source,
    commandHandle: skill.command,
    category: skill.category,
  };
}
